import { HearingPhaseBarDirective } from "../../hearing-detail/hearing-phase-bar.directive";
import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, Signal, computed, signal } from "@angular/core";
import { Validators } from "@angular/forms";
import { RouterLink } from "@angular/router";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormInputType, InputChangeEvent, InputConfig } from "@template-ui/types/ez-form.types";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { TerminologyFormIdentityDirective } from "@app/shared/terminology/terminology-form-identity.directive";
import { PHMS_DECISION_TERMINOLOGY } from "../../phms-decision.terminology";
import { HEARING_DECISION_ENTRY_TERMINOLOGY } from "./hearing-decision-entry.terminology";

export interface DecisionFormValues { finding: string; rationale: string; specialConditions: string; denialLengthYears: string; }
export function createEmptyDecisionValues(): DecisionFormValues { return { finding: "", rationale: "", specialConditions: "", denialLengthYears: "" }; }
export interface FindingOption { value: string; viewValue: string; }

/** Unsaved adjudication draft, kept separate from hearing capture autosave. */
@Component({
  selector: "phms-hearing-decision-section", standalone: true,
  imports: [HearingPhaseBarDirective, CommonModule, RouterLink, UiLibraryModule, TerminologyIdentityDirective, TerminologyFormIdentityDirective],
  templateUrl: "./hearing-decision-section.component.html", styleUrl: "./hearing-decision-section.component.scss",
})
export class HearingDecisionSectionComponent {
  readonly terminologyUsage = PHMS_DECISION_TERMINOLOGY.decisionEntry;
  readonly decisionText = createTerminologyUsageSignals(this.terminologyUsage);
  readonly entryText = createTerminologyUsageSignals(HEARING_DECISION_ENTRY_TERMINOLOGY);
  readonly formUsage = { rationale: HEARING_DECISION_ENTRY_TERMINOLOGY.rationale };
  @Input() testId = "";
  @Input() proceedingId: number | null = null;
  @Input() decisionValues: DecisionFormValues = createEmptyDecisionValues();
  @Input() canEdit!: Signal<boolean>;
  @Input() canSubmitDecision!: Signal<boolean>;
  @Input() isUnsuitable!: Signal<boolean>;
  @Input() isFinalizing!: Signal<boolean>;
  @Input() decisionError!: Signal<string | null>;
  @Input() decisionSubmitted!: Signal<boolean>;
  @Input() capturePending: Signal<boolean> = signal(false);
  @Input() captureFailed: Signal<boolean> = signal(false);
  private readonly options = signal<readonly FindingOption[]>([]);
  @Input() set findingOptions(value: readonly FindingOption[]) { this.options.set(value); }
  @Output() findingChange = new EventEmitter<string>();
  @Output() rationaleChange = new EventEmitter<string>();
  @Output() denialLengthChange = new EventEmitter<string>();
  @Output() submit = new EventEmitter<void>();

  readonly findingConfig = computed<InputConfig[]>(() => [{ name: "finding", label: "Finding *", type: EzFormInputType.RADIO_GROUP_PLAIN,
    row: 1, width: "fill", options: [...this.options()], validation: [Validators.required], validationMessages: { required: "Select a finding." } }]);
  readonly denialConfig: InputConfig[] = [{ name: "denialLengthYears", label: "Denial length (years)", type: EzFormInputType.NUMBER,
    row: 1, width: "fill", floatLabel: "always", validation: [Validators.required, Validators.pattern(/^[1-9]\d*$/), Validators.max(2147483647)],
    validationMessages: { required: "Enter a denial length in whole years.", pattern: "Enter a positive whole number of years.", max: "Enter a valid number of years." } }];
  readonly rationaleConfig = computed<InputConfig[]>(() => [{ name: "rationale", label: `${this.entryText.rationale()} *`, type: EzFormInputType.TEXTAREA,
    row: 1, width: "fill", textAreaRows: 5, floatLabel: "always", validation: [Validators.required, Validators.pattern(/\S/)],
    validationMessages: { required: "Enter the rationale for this finding.", pattern: "Enter the rationale for this finding." } }]);
  readonly conditionsConfig: InputConfig[] = [{ name: "specialConditions", label: "Special conditions (optional)", type: EzFormInputType.TEXTAREA,
    row: 1, width: "fill", textAreaRows: 5, floatLabel: "always" }];

  onInputChange(event: InputChangeEvent): void {
    if (!this.canEdit() || this.isFinalizing() || this.decisionSubmitted()
      || !["finding", "rationale", "specialConditions", "denialLengthYears"].includes(event.name)) return;
    const value = String(event.value ?? "");
    this.decisionValues[event.name as keyof DecisionFormValues] = value;
    if (event.name === "finding") this.findingChange.emit(value);
    if (event.name === "rationale") this.rationaleChange.emit(value);
    if (event.name === "denialLengthYears") this.denialLengthChange.emit(value);
  }
  requestSubmit(): void { if (this.canSubmitDecision() && !this.isFinalizing()) this.submit.emit(); }
}
